import React from "react";

import { css } from "@emotion/react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { CSS_MQ } from "../../styles/breakpoints";
import { ContentContainer } from "../_shared-components/arrangement/Container";
import { AssetType, ContentfulComponentCMSHook } from "@swo/shared";
import { SwarovskiButton } from "../_shared-components/ui-elements/SwarovskiButton";
import cn from "classnames";
import { desktopMediaQueryStr, mobileMediaQueryStr } from "../HeroImageComponent/subcomponents/ImageSourceElements";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";
import { transformImage, wrapToHtmlSourceElements } from "../_shared-components/image-related/transformImage";
import { array, option } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { responsivePlainTextStyles } from "../../styles/text-styles";
import { EXPERIMENTAL_TextWithProductNameRenderer } from "../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { ImageAndFeaturesType } from "@swo/storefront";

export const ImageAndFeaturesComponent: ReactTFC<ImageAndFeaturesType & ContentfulComponentCMSHook> = props => {
  const fontSizeType: "big" | "normal" = getFontSizeType();

  function getFontSizeType() {
    return Math.max(...props.features.map(f => f.headline.length)) >= 12 ? "normal" : "big";
  }

  const mobileImageElements = getTransformedImage(props.mobileImage, {
    requestedWidth: 600,
    noTransparentBackground: true,
    mediaQueryStr: mobileMediaQueryStr,
  });

  const desktopImageElements = getTransformedImage(props.image, {
    requestedWidth: 1400,
    noTransparentBackground: true,
    mediaQueryStr: desktopMediaQueryStr,
  });

  return (
    <ContentContainer
      className={getComponentCMSClassname(props?.sys?.id)}
      attachStyles={css`
        display: flex;

        flex-direction: column;
      `}
    >
      <picture>
        {[...mobileImageElements, ...desktopImageElements].map(s => (
          <source key={JSON.stringify(s)} srcSet={s.srcSet} type={s.type} media={s.mediaQueryStr} />
        ))}

        <img
          className={cn({ "visible-mobile": props.mobileImage, "visible-desktop": props.image })}
          css={css`
            width: auto;
            max-width: 100%;
            height: auto;
            max-height: 375px;

            margin-left: auto;
            margin-right: auto;

            ${toggleableVisibility};
          `}
          alt={
            props.image?.description ||
            props.mobileImage?.description ||
            props.image?.title ||
            props.mobileImage?.title ||
            undefined
          }
          src={props.image ? props.image.url : props.mobileImage ? props.mobileImage.url : undefined}
        />
      </picture>

      <div
        className={`features-${props.features.length}`}
        css={css`
          padding: 10px 0;

          ${CSS_MQ.atMostSm} {
            > *:not(:last-child) {
              margin-bottom: 20px;
              border-bottom: 1px solid black;
              padding-bottom: 20px;
            }
          }

          ${CSS_MQ.atLeastMd} {
            display: grid;

            &.features-1 {
              ${oneFeaturesGrid};
            }

            &.features-2 {
              ${twoFeaturesGrid};
            }

            &.features-3 {
              ${threeFeaturesGrid};
            }

            > *:not(:last-child) {
              border-right: 1px solid black;
            }

            padding: 29px 0;
            ${CSS_MQ.atLeastXl} {
              padding: 49px 57px;
            }
          }
        `}
      >
        {props.features.map((feature, idx) => {
          return (
            <React.Fragment key={`${feature.headline}-${idx}`}>
              <div
                css={css`
                  text-align: center;

                  ${CSS_MQ.atLeastMd} {
                    padding: 0 20px;
                  }
                `}
              >
                <span css={headlineStyles} className={`variant-${fontSizeType}`}>
                  <EXPERIMENTAL_TextWithProductNameRenderer text={feature.headline} />
                </span>
                <span css={subHeadlineStyles}>
                  <EXPERIMENTAL_TextWithProductNameRenderer text={feature.subHeadline} />
                </span>
                <span
                  css={css`
                    ${FONTS.SwarovskiFutura300};
                    ${fontColor.black};

                    ${responsivePlainTextStyles};
                  `}
                >
                  <EXPERIMENTAL_TextWithProductNameRenderer text={feature.text} />
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {props.callToActionButton && (
        <div css={btnStyles}>
          <SwarovskiButton
            as={"a"}
            isInternal={true}
            link={props.callToActionButton.data}
            variant={"green"}
            target={props.callToActionButton.openInNewTab ? "_blank" : undefined}
          >
            {props.callToActionButton.label}
          </SwarovskiButton>
        </div>
      )}
      {props.callToActionLink && (
        <div css={btnStyles}>
          <SwarovskiButton as={"a"} isInternal={true} link={props.callToActionLink.link} variant={"plain-underline"}>
            {props.callToActionLink.label}
          </SwarovskiButton>
        </div>
      )}
    </ContentContainer>
  );
};

const subHeadlineStyles = [
  FONTS.SwarovskiFutura500,
  fontSize.x2,
  css`
    padding-bottom: 15px;
    ${CSS_MQ.atLeastMd} {
      padding-bottom: 17px;
    }

    display: block;

    line-height: 1;
    color: ${CSS_COLORS.Black};
    text-transform: uppercase;
    margin-top: 0;
    margin-bottom: 0;
  `,
];

const headlineStyles = css`
  ${FONTS.SwarovskiFutura500};
  word-break: break-word;
  display: block;

  color: ${CSS_COLORS.Green};
  text-transform: uppercase;
  margin-bottom: 15px;
  margin-top: 0;

  &.variant-normal {
    font-size: 24px;
    letter-spacing: 0.35px;
    line-height: 26px;

    ${CSS_MQ.atLeastMd} {
      font-size: 30px;
      line-height: 32px;
    }

    ${CSS_MQ.atLeastLg} {
      font-size: 36px;
      line-height: 38px;
    }

    ${CSS_MQ.atLeastXl} {
      font-size: 40px;
      line-height: 42px;
    }
  }

  &.variant-big {
    font-size: 30px;
    letter-spacing: 0.35px;
    line-height: 26px;

    ${CSS_MQ.atLeastLg} {
      font-size: 36px;
      line-height: 32px;
    }

    ${CSS_MQ.atLeastXl} {
      font-size: 48px;
      line-height: 52px;
    }
  }
`;

const threeFeaturesGrid = css`
  grid-template-columns: repeat(3, minmax(0, 1fr));
  justify-content: center;
`;

const twoFeaturesGrid = css`
  grid-template-columns: repeat(2, minmax(0, 1fr));
  justify-content: center;

  width: calc(100% * 2 / 3);
  margin-left: auto;
  margin-right: auto;
`;

const oneFeaturesGrid = css`
  grid-template-columns: repeat(1, minmax(0, 1fr));
  justify-content: center;

  width: calc(100% * 1 / 3);
  margin-left: auto;
  margin-right: auto;
`;

const btnStyles = css`
  margin: 10px auto;
`;

const toggleableVisibility = css`
  display: none;

  &.visible-mobile {
    ${CSS_MQ.atMostSm} {
      display: block;
    }
  }

  &.visible-desktop {
    ${CSS_MQ.atLeastMd} {
      display: block;
    }
  }
`;

function getTransformedImage(
  img: AssetType | undefined,
  opts: { requestedWidth: number; noTransparentBackground: boolean; mediaQueryStr: string },
) {
  return pipe(
    img,
    option.fromNullable,
    option.map(i =>
      transformImage({
        url: i.url,
        requestedWidth: opts.requestedWidth,
        noTransparentBackground: opts.noTransparentBackground,
      }),
    ),
    option.map(wrapToHtmlSourceElements),
    option.map(elements =>
      elements.map(e => ({
        type: e.type,
        srcSet: e.srcSetCssStr,
        mediaQueryStr: opts.mediaQueryStr,
      })),
    ),
    option.getOrElseW(() => array.empty),
  );
}
